import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import Modal from "react-bootstrap/Modal";

import "./DeviceForm.css";
import { BorderColor } from "@mui/icons-material";

interface props {
	device: any;
	fetchData: any;
}

const CONFIRM_TOKEN = "SWAP";

const DeviceForm: React.FC<props> = ({ device, fetchData }) => {
	const [clients, setClients] = useState([]);
	const [sites, setSites] = useState([]);
	const [buildings, setBuildings] = useState([]);
	const [areas, setAreas] = useState([]);
	const [formMsg, setFormMsg] = useState("");
	const [formMsgColor, setFormMsgColor] = useState("");
	const [formMsgVisibility, setFormMsgVisibility] = useState(false);
	const [buildingVisibility, setBuildingVisibility] = useState(true);
	const [areaVisibility, setAreaVisibility] = useState(true);
	const [submitBtnVisibility, setSubmitBtnVisibility] = useState(true);

	const [adminDevices, setAdminDevices] = useState<any[]>([]);
	const [swapTargetId, setSwapTargetId] = useState<string>("");
	const [showConfirm, setShowConfirm] = useState(false);
	const [confirmText, setConfirmText] = useState("");
	const [swapMsg, setSwapMsg] = useState("");
	const [swapMsgColor, setSwapMsgColor] = useState("");
	const [swapping, setSwapping] = useState(false);

	const serverUrl = `${process.env.REACT_APP_PROTOCOL}://${process.env.REACT_APP_SERVER_HOST}:${process.env.REACT_APP_SERVER_HOST_PORT}`;

	const fetchEntityData = useCallback(
		(param: string, body: any) => {
			axios
				.post(`${serverUrl}/api/${param}`, body, {
					withCredentials: true,
				})
				.then(async function (response) {
					const apiData = await response.data;

					const setData = () => {
						switch (param) {
							case "clients":
								setClients(apiData.data);
								return;
							case "sites":
								setSites(apiData.data);
								return;
							case "buildings":
								setBuildings(apiData.data);
								return;
							case "areas":
								setAreas(apiData.data);
								return;
						}
					};

					apiData.status === "success"
						? setData()
						: console.log("error fetching data");
				})
				.catch(function (error) {
					console.error(error);
				});
		},
		[serverUrl]
	);

	useEffect(() => {
		fetchEntityData("clients", {});
		fetchEntityData("sites", { clientId: device.client });
		fetchEntityData("buildings", { siteId: device.site });
		fetchEntityData("areas", { buildingId: device.building });
	}, [device.building, device.client, device.site, fetchEntityData]);

	useEffect(() => {
		axios
			.get(`${serverUrl}/api/devices-admin`, { withCredentials: true })
			.then((response) => {
				const apiData = response.data;
				if (apiData.status === "success" && Array.isArray(apiData.data)) {
					setAdminDevices(
						apiData.data.filter(
							(d: any) => Number(d.deviceId) !== Number(device.deviceId),
						),
					);
				}
			})
			.catch((err) => console.error("devices-admin fetch failed", err));
	}, [serverUrl, device.deviceId]);

	const targetDevice = adminDevices.find(
		(d) => String(d.deviceId) === String(swapTargetId),
	);

	const performSwap = () => {
		if (!swapTargetId) return;
		setSwapping(true);
		axios
			.post(
				`${serverUrl}/api/device-swap-cpu`,
				{
					deviceId: Number(device.deviceId),
					targetDeviceId: Number(swapTargetId),
				},
				{ withCredentials: true },
			)
			.then((response) => {
				const apiData = response.data;
				if (apiData.status === "success") {
					setSwapMsg(
						`CPU IDs swapped. This device now reports as cpuId ${apiData.data?.newCpuIdA ?? "(updated)"}.`,
					);
					setSwapMsgColor("#32cd32");
					setShowConfirm(false);
					setConfirmText("");
					setSwapTargetId("");
					fetchData();
				} else {
					setSwapMsg(
						`Swap failed: ${apiData.errMsg?.desc ?? apiData.status ?? "unknown error"}`,
					);
					setSwapMsgColor("#ff0000");
				}
			})
			.catch((err) => {
				console.error(err);
				setSwapMsg(
					`Swap failed: ${err?.response?.data?.errMsg?.desc ?? err.message ?? "request error"}`,
				);
				setSwapMsgColor("#ff0000");
			})
			.finally(() => setSwapping(false));
	};

	const handleSubmit = (values: any) => {
		const body = {
			name: values.name,
			description: values.description,
			volume: Number(values.volume),
			area: Number(values.area),
			deviceId: Number(values.deviceId),
		};

		axios
			.post(`${serverUrl}/api/device-update`, body, {
				withCredentials: true,
			})
			.then(async function (response) {
				const apiData = await response.data;
				if (apiData.data === "updated" && apiData.status === "success") {
					setFormMsg("Updated");
					setFormMsgColor("#32cd32");
					setFormMsgVisibility(true);
					fetchData();
				} else {
					setFormMsg("Error Updating");
					setFormMsgColor("#ff0000");
					setFormMsgVisibility(true);
				}
			})
			.catch(function (error) {
				console.error(error);
				setFormMsg("Error Updating");
				setFormMsgColor("#ff0000");
				setFormMsgVisibility(true);
			});
	};
	return (
		<div className="form-div">
			<Formik
				initialValues={{
					name: device.name || "",
					description: device.description || "",
					volume: device.volume || "",
					client: device.client || "",
					site: device.site || "",
					building: device.building || "",
					area: device.area || null,
					deviceId: device.deviceId || "",
				}}
				validationSchema={Yup.object({
					name: Yup.string().required("Required"),
					description: Yup.string().max(
						199,
						"Description must be less than 200 characters"
					),
					volume: Yup.number().min(1, "Invalid number, number must be > 0"),
					client: Yup.string().required("Required"),
					site: Yup.string().required("Required"),
					building: Yup.string().required("Required"),
					area: Yup.string().required("Required"),
				})}
				onSubmit={(values) => handleSubmit(values)}
			>
				{({ setFieldValue, values }) => (
					<Form
						className="device-form"
						onKeyDown={(e) => {
							if (e.key === "Enter") {
								e.preventDefault();
							}
						}}
					>
						<div className="identity-section">
							<h6 className="identity-heading">Identity</h6>
							<div className="identity-row">
								<span>Id:</span>
								<span>{device.deviceId ?? "—"}</span>
							</div>
							<div className="identity-row">
								<span>cpuId:</span>
								<span>{device.cpuId ?? "—"}</span>
							</div>
						</div>

						<label>Device name</label>
						<Field
							name="name"
							type="text"
							placeholder="Enter device name"
						/>
						<p className="input-err">
							<ErrorMessage name="name" />
						</p>

						<label>Description</label>
						<Field
							name="description"
							as="textarea"
							placeholder="Enter device description"
							className="device-form-textarea"
						/>
						<p className="input-err">
							<ErrorMessage name="description" />
						</p>

						<label>Tank Volume (ℓ)</label>
						<Field
							name="volume"
							type="number"
							placeholder="Enter tank volume"
						/>
						<p className="input-err">
							<ErrorMessage name="volume" />
						</p>
						<div>
							<div>
								<label>Client</label>
								<Field
									className="my-select"
									as="select"
									name="client"
									onChange={(e: any) => {
										const selectedValue = e.target.value;
										setFieldValue("client", selectedValue);
										setFieldValue("site", "");
										fetchEntityData("sites", { clientId: selectedValue });
										setFormMsgVisibility(false);
										setBuildingVisibility(false);
										setAreaVisibility(false);
										setSubmitBtnVisibility(false);
									}}
								>
									{clients.map((item: any) => (
										<option
											key={item.id}
											value={item.id}
										>
											{item.name}
										</option>
									))}
								</Field>

								<p className="input-err">
									<ErrorMessage name="client" />
								</p>
							</div>
							<div>
								<label>Site</label>
								<Field
									className="my-select"
									as="select"
									name="site"
									onChange={(e: any) => {
										const selectedValue = e.target.value;
										setFieldValue("site", selectedValue);
										setFieldValue("building", "");
										fetchEntityData("buildings", { siteId: selectedValue });
										setFormMsgVisibility(false);
										setBuildingVisibility(true);
										setAreaVisibility(false);
										setSubmitBtnVisibility(false);
									}}
								>
									<option value=""></option>
									{sites.map((item: any) => (
										<option
											key={item.id}
											value={item.id}
										>
											{item.name}
										</option>
									))}
								</Field>
								<p className="input-err">
									<ErrorMessage name="site" />
								</p>
							</div>
							{buildingVisibility ? (
								<>
									<div>
										<label>Building</label>
										<Field
											className="my-select"
											as="select"
											name="building"
											onChange={(e: any) => {
												const selectedValue = e.target.value;
												setFieldValue("building", selectedValue);
												setFieldValue("area", "");
												fetchEntityData("areas", { buildingId: selectedValue });
												setFormMsgVisibility(false);
												setBuildingVisibility(true);
												setAreaVisibility(true);
												setSubmitBtnVisibility(false);
											}}
										>
											<option value=""></option>
											{buildings.map((item: any) => (
												<option
													key={item.id}
													value={item.id}
												>
													{item.name}
												</option>
											))}
										</Field>
										<p className="input-err">
											<ErrorMessage name="building" />
										</p>
									</div>
								</>
							) : null}
							{areaVisibility ? (
								<>
									<div>
										<label>Area</label>
										<Field
											className="my-select"
											as="select"
											name="area"
											onChange={(e: any) => {
												const selectedValue = e.target.value;
												setFieldValue("area", selectedValue);
												setSubmitBtnVisibility(true);
											}}
										>
											<option value=""></option>
											{areas.map((item: any) => (
												<option
													key={item.id}
													value={item.id}
												>
													{item.name}
												</option>
											))}
										</Field>
										<p className="input-err">
											<ErrorMessage name="area" />
										</p>
									</div>
								</>
							) : null}
							{formMsgVisibility ? (
								<p style={{ color: formMsgColor }}>{formMsg}</p>
							) : null}
						</div>

						{submitBtnVisibility ? <button type="submit">Save</button> : null}
					</Form>
				)}
			</Formik>

			{true ? (
				<div className="merge-section">
					<h5>Merge / Swap CPU with another device</h5>
					<p style={{ fontSize: 12, margin: "4px 0 10px" }}>
						Re-home a physical chip onto a different device record by swapping
						the cpuIds between this device and the one you pick below. Future
						telemetry from the swapped chip will flow into the other record;
						existing history stays attached to its current record.
					</p>

					<label>Target device</label>
					<select
						className="my-select"
						value={swapTargetId}
						onChange={(e) => {
							setSwapTargetId(e.target.value);
							setSwapMsg("");
						}}
					>
						<option value="">— select a device —</option>
						{adminDevices.map((d: any) => (
							<option key={d.deviceId} value={d.deviceId}>
								{(d.clientName || "?")}/{(d.siteName || "?")}/
								{(d.buildingName || "?")}/{(d.areaName || "?")} — {d.name}{" "}
								(cpu {d.cpuId})
							</option>
						))}
					</select>

					{targetDevice ? (
						<>
							<div className="merge-cpu-row">
								<span>{device.name || "this device"} cpuId:</span>
								<span>{device.cpuId ?? "(unknown)"}</span>
							</div>
							<div className="merge-cpu-row">
								<span>{targetDevice.name} cpuId:</span>
								<span>{targetDevice.cpuId}</span>
							</div>
							<p className="merge-warning">
								⚠ After swap, "{device.name}" will be linked to cpuId{" "}
								{targetDevice.cpuId} and "{targetDevice.name}" will be linked to
								cpuId {device.cpuId ?? "(this device's cpuId)"}. Historical
								temperature data and alarms do NOT move — only the hardware
								link. This action is irreversible from the UI.
							</p>
							<button
								type="button"
								className="merge-btn"
								onClick={() => {
									setConfirmText("");
									setSwapMsg("");
									setShowConfirm(true);
								}}
							>
								Swap CPUs…
							</button>
						</>
					) : null}

					{swapMsg ? (
						<p style={{ color: swapMsgColor, marginTop: 10 }}>{swapMsg}</p>
					) : null}
				</div>
			) : null}

			<Modal
				show={showConfirm}
				onHide={() => (swapping ? null : setShowConfirm(false))}
				backdrop={swapping ? "static" : true}
			>
				<Modal.Header closeButton={!swapping}>
					<Modal.Title>Confirm CPU swap</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{targetDevice ? (
						<>
							<p>
								You are about to swap cpuIds between:
							</p>
							<ul style={{ fontSize: 13, marginBottom: 12 }}>
								<li>
									<b>{device.name}</b> (currently cpuId{" "}
									<code>{device.cpuId ?? "?"}</code>)
								</li>
								<li>
									<b>{targetDevice.name}</b> (currently cpuId{" "}
									<code>{targetDevice.cpuId}</code>)
								</li>
							</ul>
							<p className="merge-confirm-warning">
								This re-homes physical hardware between device records. Type{" "}
								<code>{CONFIRM_TOKEN}</code> below to confirm.
							</p>
							<input
								className="merge-confirm-input"
								type="text"
								value={confirmText}
								onChange={(e) => setConfirmText(e.target.value)}
								placeholder={`Type ${CONFIRM_TOKEN} to confirm`}
								autoFocus
								disabled={swapping}
							/>
							<div className="merge-confirm-buttons">
								<button
									type="button"
									className="cancel"
									onClick={() => setShowConfirm(false)}
									disabled={swapping}
								>
									Cancel
								</button>
								<button
									type="button"
									className="confirm"
									disabled={confirmText !== CONFIRM_TOKEN || swapping}
									onClick={performSwap}
								>
									{swapping ? "Swapping…" : "Confirm swap"}
								</button>
							</div>
						</>
					) : null}
				</Modal.Body>
			</Modal>
		</div>
	);
};

export default DeviceForm;
